import classNames from "classnames";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React from "react";
import { RoleAccessRecordSnapshotType, TreeRow } from "../models/access-store";
import styles from "./FunctionalityTree.module.scss";

export const FunctionalityTree = ({ onRowSelect, currentRow, rows }: FunctionalityTreeProps) => {
  return (
    <div className={`functionality-tree ${styles.tree}`}>
      <table>
        <tbody>{rendered(currentRow, rows, onRowSelect)}</tbody>
      </table>
    </div>
  );
};

export const renderRows =
  (addModuleName: boolean, positionTable?: boolean, level4?: (row: TreeRow, key: number) => JSX.Element) =>
  (currentRow: number, iteratee: TreeRow[], onRowSelect?: (index: number) => void) => {
    const used: TStringMap<boolean> = {};
    const selected = iteratee[currentRow];

    let increment = 1;
    const rows = iteratee.map((row, rowIndex) => {
      const cells: JSX.Element[] = [];

      if (row.level1 && !used[row.level1] && addModuleName) {
        const span = iteratee.filter((r) => r.level1 === row.level1).length;
        let className = classNames({
          [styles.current]: selected && selected.level1 === row.level1,
        });

        cells.push(
          <td rowSpan={span} className={`${styles.vertical} ${className}`} key={increment++}>
            <div className={styles.text}>{row.level1}</div>
          </td>
        );

        used[row.level1] = true;
      }

      if (row.level2 && !used[row.level1 + row.level2]) {
        const isVertical = !row.level1;
        const span = iteratee.filter((r) => r.level1 === row.level1 && r.level2 === row.level2).length;
        let className = classNames({
          [styles.current]: selected && selected.level1 === row.level1 && selected.level2 === row.level2,
          [styles.vertical]: isVertical,
        });

        if (!isVertical || addModuleName) {
          cells.push(
            <td rowSpan={span} key={increment++} className={`centered ${className}`}>
              <div className={!row.level1 ? styles.text : ""}>{row.level2}</div>
            </td>
          );

          used[row.level1 + row.level2] = true;
        }
      }

      // always
      {
        const colSpan = row.level1 && row.level2 ? 1 : !row.level1 && !row.level2 ? 3 : 2;
        let className = classNames({
          [styles.current]: selected && selected.functionality === row.functionality,
          [styles.small]: colSpan === 1,
        });

        cells.push(
          <td
            colSpan={colSpan}
            key={increment++}
            className={`centered ${className} ${positionTable ? styles.level3Pos : styles.level3}`}
            onClick={onRowSelect ? () => onRowSelect(rowIndex) : undefined}
          >
            {row.level3}
          </td>
        );

        level4 && cells.push(level4(row, ++increment));
      }

      return <tr key={increment++}>{cells}</tr>;
    });

    return rows;
  };

export const renderAsMenuRows = (
  currentRow: number,
  iteratee: TreeRow[],
  access: RoleAccessRecordSnapshotType[],
  onToggle: (functionality: string, state: boolean) => void
) => {
  const used: TStringMap<boolean> = {};

  let increment = 1;

  const mainCell = (
    <div className={styles.rowName} key={increment++}>
      <div className={styles.text}>{iteratee[0].level1}</div>
    </div>
  );
  used[iteratee[0].level1] = true;

  const mainRowCells = iteratee.map((row, rowIndex) => {
    const value = access.find((a) => a.name === row.functionality);
    const can = !!value && value.value;

    if (row.level1 && !used[row.level1] && !row.level3) {
      used[row.level1] = true;
      return (
        <div className={styles.rowName} key={increment++}>
          <div className={styles.text}>{row.level1}</div>
        </div>
      );
    }

    if (row.level2 && !used[row.level1 + row.level2] && !row.level3) {
      used[row.level1 + row.level2] = true;
      return value && value.overwritten ? (
        <PlanrButton
          key={increment++}
          type={"blueish"}
          size="small"
          onClick={() => onToggle(row.functionality, !can)}
          style={{ maxHeight: "30px", fontSize: "12px" }}
        >
          {row.level2}
        </PlanrButton>
      ) : can ? (
        <PlanrButton
          key={increment++}
          type={"primary"}
          size="small"
          onClick={() => onToggle(row.functionality, !can)}
          style={{ maxHeight: "30px", fontSize: "12px" }}
        >
          {row.level2}
        </PlanrButton>
      ) : (
        <PlanrButton
          key={increment++}
          type={"secondary"}
          size="small"
          onClick={() => onToggle(row.functionality, !can)}
          style={{ maxHeight: "30px", fontSize: "12px" }}
        >
          {row.level2}
        </PlanrButton>
      );
    }
    return null;
  });

  const mainRow = (
    <div className={styles.mainRow} key={increment++}>
      {[mainCell, ...mainRowCells]}
    </div>
  );

  const rows: JSX.Element[] = [];

  let cells: JSX.Element[] = [];
  iteratee.forEach((row, rowIndex) => {
    const value = access.find((a) => a.name === row.functionality);
    const can = !!value && value.value;

    if (row.level1 && !used[row.level1]) {
      cells.push(
        <div className={styles.rowName} key={increment++}>
          <div className={styles.text}>{row.level1}</div>
        </div>
      );

      used[row.level1] = true;
    }

    if (row.level2 && !used[row.level1 + row.level2]) {
      if (cells.length > 0) {
        rows.push(
          <div className={styles.secondaryRow} key={increment++}>
            {cells}
          </div>
        );
        cells = [];
      }

      cells.push(
        <div key={increment++} className={styles.rowName}>
          <div className={styles.text}>{row.level2}</div>
        </div>
      );

      used[row.level1 + row.level2] = true;
    }

    if (row.level3 && !used[row.level1 + row.level2 + row.level3]) {
      cells.push(
        value && value.overwritten ? (
          <PlanrButton
            key={increment++}
            type={"blueish"}
            size="small"
            onClick={() => onToggle(row.functionality, !can)}
            style={{ maxHeight: "30px", fontSize: "12px" }}
          >
            {row.level3}
          </PlanrButton>
        ) : can ? (
          <PlanrButton
            key={increment++}
            type={"primary"}
            size="small"
            onClick={() => onToggle(row.functionality, !can)}
            style={{ maxHeight: "30px", fontSize: "12px" }}
          >
            {row.level3}
          </PlanrButton>
        ) : (
          <PlanrButton
            key={increment++}
            type={"secondary"}
            size="small"
            onClick={() => onToggle(row.functionality, !can)}
            style={{ maxHeight: "30px", fontSize: "12px" }}
          >
            {row.level3}
          </PlanrButton>
        )
        // <div
        //   key={increment++}
        //   className={value && value.overwritten ? "access-value overwritten" : can ? "access-true" : "access-value"}
        //   onClick={() => onToggle(row.functionality, !can)}
        // >
        //   <div>{row.level3}</div>
        // </div>
      );

      used[row.level1 + row.level2 + row.level3] = true;
    }

    if (rowIndex === iteratee.length - 1) {
      if (cells.length > 0) {
        rows.push(
          <div className={styles.secondaryRow} key={increment++}>
            {cells}
          </div>
        );
      }
    }
  });
  return [mainRow, ...rows];
};

const rendered = renderRows(true);

interface FunctionalityTreeProps {
  currentRow: number;
  onRowSelect: (index: number) => void;
  rows: TreeRow[];
}
